import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../Redux/hooks';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { routes } from '../../../Utils/routes';
import './PremiumSubnav.css';

const ENTRIES: { to: string; label: TranslationKey; end?: boolean }[] = [
  { to: routes.premiumArea, label: 'navPremium', end: true },
  { to: routes.receipts, label: 'navReceipts' },
  { to: routes.receiptReports, label: 'navReceiptReports' },
  { to: routes.business, label: 'businessTitle' },
  { to: routes.openFormat, label: 'openFormatTitle' },
  { to: routes.reports, label: 'navReports' },
  { to: routes.reminders, label: 'navReminders' },
  { to: routes.paymentDocuments, label: 'navPaymentDocuments' }
];

/**
 * The premium screens as one place rather than seven unrelated URLs.
 *
 * The admin entry is appended only for an admin. That is presentation, not
 * protection: every admin route refuses a caller who is not on the server's
 * list, whatever this renders.
 */
export function PremiumSubnav() {
  const { tr } = useTranslation();
  const isAdmin = useAppSelector(state => state.session.subscription?.isAdmin ?? false);
  const entries = isAdmin
    ? [...ENTRIES, { to: routes.admin, label: 'navAdmin' as TranslationKey }]
    : ENTRIES;
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // Seven-plus tabs do not fit a phone width, so the strip scrolls. Left to
  // itself it opens at scrollLeft 0 regardless of which tab is current, so a
  // tutor landing deep in the list (Reports, Reminders) sees the active pill
  // sliced off at the edge instead of highlighted. Reveal it on every route
  // change instead of leaving that to a manual swipe.
  useEffect(() => {
    const active = navRef.current?.querySelector<HTMLElement>('.premium-subnav-item.is-active');
    // jsdom (the test environment) does not implement scrollIntoView at all.
    if (typeof active?.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [pathname]);

  return (
    <nav className="premium-subnav" aria-label={tr('navPremium')} ref={navRef}>
      {entries.map(entry => (
        <NavLink
          key={entry.to}
          to={entry.to}
          end={entry.end ?? false}
          className={({ isActive }) => (isActive ? 'premium-subnav-item is-active' : 'premium-subnav-item')}
        >
          {tr(entry.label)}
        </NavLink>
      ))}
    </nav>
  );
}
